import { Suspense, useCallback, useEffect, useMemo, useState, type ComponentType } from "react";
import { useLocation } from "react-router-dom";
import { applicationCodeForPath } from "../../utils/license";
import RemoteErrorBoundary from "../error-boundary";
import RemoteFailurePanel from "../remote-failure-panel";

export type ProductSettingsSurface = "usage-overview" | "usage-limits" | "billing-plan";
export interface ProductSettingsContributionProps {
  surface: ProductSettingsSurface;
  currentPlan?: any;
  activeAddons?: any[];
  scheduledDowngrade?: any;
  onUpgrade?: () => void;
  onSelectPlan?: (plan: any) => void;
  onSelectDowngrade?: (plan: any) => void;
  onManageSubscription?: () => void;
  onViewUsage?: () => void;
  onViewAddons?: () => void;
}
type RemoteDefinition = { remote: string; exposedModule: string; load: () => Promise<{ default?: ComponentType<ProductSettingsContributionProps> }> };
const REMOTES: Record<string, RemoteDefinition> = {
  AGENT_LAB: { remote: "art_agentlab", exposedModule: "art_agentlab/platform_settings", load: () => import("art_agentlab/platform_settings") },
  LIVE_CONNECT: { remote: "art_liveconnect", exposedModule: "art_liveconnect/platform_settings", load: () => import("art_liveconnect/platform_settings") },
  DATALAKE: { remote: "art_datalake", exposedModule: "art_datalake/platform_settings", load: () => import("art_datalake/platform_settings") },
};

export default function ProductSettingsLoader(props: ProductSettingsContributionProps) {
  const location = useLocation();
  const applicationCode = applicationCodeForPath(location.pathname).toUpperCase();
  const definition = useMemo(() => REMOTES[applicationCode], [applicationCode]);
  const [Contribution, setContribution] = useState<ComponentType<ProductSettingsContributionProps> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => { setContribution(null); setError(null); setAttempt((current) => current + 1); }, []);

  useEffect(() => {
    let active = true;
    setContribution(null);
    setError(null);
    if (!definition) return () => { active = false; };
    definition.load().then((module) => {
      if (!module.default) throw new Error(`${definition.exposedModule} did not export a default component`);
      if (active) setContribution(() => module.default as ComponentType<ProductSettingsContributionProps>);
    }).catch((reason) => { if (active) setError(reason); });
    return () => { active = false; };
  }, [attempt, definition]);

  if (!definition) return <div data-qa="product-settings-unavailable">Settings are not available for {applicationCode}.</div>;
  if (error) return <RemoteFailurePanel remote={definition.remote} exposedModule={definition.exposedModule} phase="module" error={error} onRetry={retry} />;
  if (!Contribution) return <div>Loading {applicationCode.replace(/_/g, " ").toLowerCase()} settings…</div>;
  return (
    <Suspense fallback={<div>Loading product settings…</div>}>
      <RemoteErrorBoundary key={`${applicationCode}-${attempt}`} remote={definition.remote} exposedModule={definition.exposedModule} onRetry={retry}>
        <Contribution {...props} />
      </RemoteErrorBoundary>
    </Suspense>
  );
}
